import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StatusDataLoader from "../../services/StatusDataLoader";
import ContentArea from "../ContentArea/ContentArea";
import StatusMessage from "../StatusMessage/StatusMessage";
import Layout from "./Layout/Layout";
import {
  PERSPECTIVE_RELEVANCE_RANGE,
  PERSPECTIVE_INPUT_DECIMALS,
  PerspectiveInsight,
  ConversionCurrency,
  buildPerspectiveSnapshot,
  convertFromBtcInput,
  convertFromUsdInput,
  formatEditableNumber,
  getValidCurrency,
  isValidNumericInput,
  parseInputNumber,
} from "../../utils/Calculators/ConversionPerspectiveCalculator";
import { CONVERSION_REFERENCE_DATA } from "../../utils/Calculators/ConversionReferenceData";
import { useSeo } from "../../utils/Hooks/useSeo";
import "./ConversionPage.css";

const formatPlainNumber = (value: number) =>
  value.toFixed(PERSPECTIVE_INPUT_DECIMALS).replace(/\.?0+$/, "");

const formatInputWithGrouping = (value: string) => {
  if (!value) {
    return value;
  }

  const cleaned = value.replace(/[ ,]/g, "");
  if (cleaned === ".") {
    return value;
  }

  const parts = cleaned.split(".");
  if (parts.length > 2) {
    return value;
  }

  const [intPartRaw, fracPart] = parts;
  const intPart = intPartRaw || "0";
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const hasTrailingDot = value.trim().endsWith(".");

  if (hasTrailingDot && !fracPart) {
    return `${groupedInt}.`;
  }

  return fracPart ? `${groupedInt}.${fracPart}` : groupedInt;
};

type MarketState = {
  btcUsdPrice: number;
  btcMarketCapUsd: number;
};

type PerspectiveMode = "relevant" | "complete";

export const ConversionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [market, setMarket] = useState<MarketState | undefined>(undefined);
  const [usdInput, setUsdInput] = useState("");
  const [btcInput, setBtcInput] = useState("");
  const [lastEditedCurrency, setLastEditedCurrency] = useState<ConversionCurrency>("USD");
  const [perspectiveMode, setPerspectiveMode] = useState<PerspectiveMode>("relevant");
  const [hasHydratedFromQuery, setHasHydratedFromQuery] = useState(false);
  const [copiedInsightId, setCopiedInsightId] = useState<string | undefined>(undefined);

  useSeo({
    title: "BTC and USD Converter",
    description:
      "Convert BTC and USD instantly and compare your value against market cap, commodities, and real-world reference benchmarks.",
    canonicalPath: "/convert",
  });

  useEffect(() => {
    const loadStatus = async () => {
      const status = await StatusDataLoader.getStatusData();
      const btcUsdPrice = status?.market?.btcPriceUsd ?? 0;
      const btcMarketCapUsd = status?.market?.btcMarketCapUsd ?? 0;

      setMarket({ btcUsdPrice, btcMarketCapUsd });
      setIsLoading(false);
    };

    loadStatus();
  }, []);

  const syncUrl = useCallback(
    (value: string, currency: ConversionCurrency) => {
      const parsed = parseInputNumber(value);
      if (parsed === undefined) {
        setSearchParams({}, { replace: true });
        return;
      }

      const nextParams = new URLSearchParams();
      nextParams.set("value", formatPlainNumber(parsed));
      nextParams.set("currency", currency);
      setSearchParams(nextParams, { replace: true });
    },
    [setSearchParams],
  );

  const syncFromUsd = useCallback(
    (
      nextUsdInput: string,
      options: { updateUrl: boolean },
    ) => {
      setUsdInput(formatInputWithGrouping(nextUsdInput));
      setLastEditedCurrency("USD");

      const btcValue = convertFromUsdInput(nextUsdInput, market?.btcUsdPrice ?? 0);
      if (btcValue === undefined) {
        setBtcInput("");
      } else {
        setBtcInput(formatEditableNumber(btcValue, PERSPECTIVE_INPUT_DECIMALS));
      }

      if (options.updateUrl) {
        syncUrl(nextUsdInput, "USD");
      }
    },
    [market?.btcUsdPrice, syncUrl],
  );

  const syncFromBtc = useCallback(
    (
      nextBtcInput: string,
      options: { updateUrl: boolean },
    ) => {
      setBtcInput(formatInputWithGrouping(nextBtcInput));
      setLastEditedCurrency("BTC");

      const btcValue = convertFromBtcInput(nextBtcInput);
      if (btcValue === undefined) {
        setUsdInput("");
      } else {
        const usdValue = btcValue * (market?.btcUsdPrice ?? 0);
        setUsdInput(formatEditableNumber(usdValue, PERSPECTIVE_INPUT_DECIMALS));
      }

      if (options.updateUrl) {
        syncUrl(nextBtcInput, "BTC");
      }
    },
    [market?.btcUsdPrice, syncUrl],
  );

  useEffect(() => {
    if (hasHydratedFromQuery || isLoading || !market?.btcUsdPrice) {
      return;
    }

    const queryCurrency = getValidCurrency(searchParams.get("currency")) ?? "USD";
    const queryValue = searchParams.get("value");

    if (queryValue && isValidNumericInput(queryValue)) {
      const formattedValue = formatInputWithGrouping(queryValue);
      if (queryCurrency === "USD") {
        syncFromUsd(formattedValue, { updateUrl: false });
      } else {
        syncFromBtc(formattedValue, { updateUrl: false });
      }
    }

    setHasHydratedFromQuery(true);
  }, [
    hasHydratedFromQuery,
    isLoading,
    market?.btcUsdPrice,
    searchParams,
    syncFromBtc,
    syncFromUsd,
  ]);

  const onUsdInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (!isValidNumericInput(nextValue)) {
      return;
    }

    syncFromUsd(nextValue, { updateUrl: true });
  };

  const onBtcInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (!isValidNumericInput(nextValue)) {
      return;
    }

    syncFromBtc(nextValue, { updateUrl: true });
  };

  const btcValueForPerspective = useMemo(() => {
    if (!market?.btcUsdPrice) {
      return undefined;
    }

    if (lastEditedCurrency === "USD") {
      return convertFromUsdInput(usdInput, market.btcUsdPrice);
    }

    return convertFromBtcInput(btcInput);
  }, [
    btcInput,
    lastEditedCurrency,
    market?.btcUsdPrice,
    usdInput,
  ]);

  const perspective = useMemo(() => {
    if (!market?.btcUsdPrice || !btcValueForPerspective || btcValueForPerspective <= 0) {
      return undefined;
    }

    return buildPerspectiveSnapshot({
      btcValue: btcValueForPerspective,
      btcUsdPrice: market.btcUsdPrice,
      btcMarketCapUsd: market.btcMarketCapUsd,
    });
  }, [btcValueForPerspective, market?.btcMarketCapUsd, market?.btcUsdPrice]);

  const relevantInsights = useMemo(() => {
    if (!perspective) {
      return [];
    }

    return perspective.sections
      .flatMap((section) => section.insights)
      .filter((insight) => {
        if (typeof insight.relevanceValue !== "number" || !Number.isFinite(insight.relevanceValue)) {
          return false;
        }

        return (
          insight.relevanceValue > PERSPECTIVE_RELEVANCE_RANGE.min &&
          insight.relevanceValue < PERSPECTIVE_RELEVANCE_RANGE.max
        );
      });
  }, [perspective]);

  const relevantAnimationKey = useMemo(() => {
    if (!relevantInsights.length) {
      return "empty";
    }

    return relevantInsights
      .map((insight) => `${insight.id}:${insight.value}`)
      .join("|");
  }, [relevantInsights]);

  const buildInsightShareHref = useCallback(
    (insightId: string) => {
      if (!btcValueForPerspective || btcValueForPerspective <= 0) {
        return undefined;
      }

      const queryParams = new URLSearchParams();
      queryParams.set("btc", formatPlainNumber(btcValueForPerspective));

      if (market?.btcUsdPrice) {
        queryParams.set("price", formatPlainNumber(market.btcUsdPrice));
      }

      if (market?.btcMarketCapUsd) {
        queryParams.set("mcap", formatPlainNumber(market.btcMarketCapUsd));
      }

      return `/perspective/${encodeURIComponent(insightId)}?${queryParams.toString()}`;
    },
    [btcValueForPerspective, market?.btcMarketCapUsd, market?.btcUsdPrice],
  );

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      return copied;
    } catch {
      return false;
    }
  }, []);

  const onCopyShareLink = useCallback(
    async (insightId: string) => {
      const relativeHref = buildInsightShareHref(insightId);
      if (!relativeHref) {
        return;
      }

      const fullUrl = `${window.location.origin}${relativeHref}`;
      const copied = await copyToClipboard(fullUrl);

      if (!copied) {
        return;
      }

      setCopiedInsightId(insightId);
      window.setTimeout(() => {
        setCopiedInsightId((current) =>
          current === insightId ? undefined : current,
        );
      }, 1600);
    },
    [buildInsightShareHref, copyToClipboard],
  );

  const renderInsightCard = (
    insight: PerspectiveInsight,
    cardIndex: number,
    key?: string,
  ) => {
    const shareHref = buildInsightShareHref(insight.id);

    return (
      <article
        className="insight-card"
        key={key ?? insight.id}
        style={{ "--card-index": cardIndex } as React.CSSProperties}
      >
        <div className="insight-title">{insight.title}</div>
        <div className="insight-value">{insight.value}</div>
        <div className="insight-note">{insight.note}</div>
        <div className="insight-actions">
          {shareHref ? (
            <a
              className="insight-share-link"
              href={shareHref}
              onClick={(event) => {
                event.preventDefault();
                void onCopyShareLink(insight.id);
              }}
            >
              {copiedInsightId === insight.id ? "Copied" : "Copy Link"}
            </a>
          ) : (
            <span className="insight-share-link disabled" aria-disabled>
              Copy Link
            </span>
          )}
        </div>
      </article>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="progress-circle">
          <progress className="pure-material-progress-circular" />
        </div>
      );
    }

    if (!market?.btcUsdPrice) {
      return (
        <StatusMessage
          title="Converter data is temporarily unavailable."
          description="Please try reloading in a minute."
          minHeightRem={20}
        />
      );
    }

    return (
      <div className="conversion-page">
        <section className="page-intro">
          <h1>BTC and USD Converter</h1>
          <p>
            Convert in either direction and view practical Bitcoin-denominated
            perspective cards from curated benchmark datasets.
          </p>
        </section>

        <section className="conversion-main-card">
          <div className="conversion-main-top">
            <div>
              <h2 className="conversion-title">BTC <span>/</span> USD Conversion Desk</h2>
              <p className="conversion-subtitle">
                Type in either box. Conversion updates in real time and syncs to the URL.
              </p>
            </div>
            <div className="market-pill">
              1 BTC = ${market.btcUsdPrice.toLocaleString("en-US", { maximumFractionDigits: 2 })}
            </div>
          </div>

          <div className="conversion-input-grid">
            <label className="conversion-input-card">
              <span className="input-label">USD side</span>
              <div className="input-row">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Type amount"
                  value={usdInput}
                  onChange={onUsdInputChange}
                />
                <span className="currency-badge">USD</span>
              </div>
            </label>

            <label className="conversion-input-card">
              <span className="input-label">BTC side</span>
              <div className="input-row">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Type amount"
                  value={btcInput}
                  onChange={onBtcInputChange}
                />
                <span className="currency-badge">BTC</span>
              </div>
            </label>
          </div>
        </section>

        <section className="conversion-perspective">
          <div className="perspective-header">
            <div>
              <h2>Perspective</h2>
            </div>
            <div className="data-tag">Based on data available on {CONVERSION_REFERENCE_DATA.dataAsOf}</div>
          </div>

          {!perspective && (
            <StatusMessage
              title="Enter a value to unlock perspective mode."
              description="Try any positive USD or BTC amount to get started."
              minHeightRem={8}
            />
          )}

          {perspective && (
            <div>
              <div className="perspective-mode-controls">
                <div className="perspective-mode-toggle" role="tablist" aria-label="Perspective view mode">
                  <button
                    type="button"
                    className={perspectiveMode === "relevant" ? "mode-toggle is-active" : "mode-toggle"}
                    onClick={() => setPerspectiveMode("relevant")}
                    aria-pressed={perspectiveMode === "relevant"}
                  >
                    Relevant
                  </button>
                  <button
                    type="button"
                    className={perspectiveMode === "complete" ? "mode-toggle is-active" : "mode-toggle"}
                    onClick={() => setPerspectiveMode("complete")}
                    aria-pressed={perspectiveMode === "complete"}
                  >
                    Complete
                  </button>
                </div>
                {perspectiveMode === "relevant" && (
                  <div className="perspective-range-note">
                    Showing cards with relevance between {PERSPECTIVE_RELEVANCE_RANGE.min} and{" "}
                    {PERSPECTIVE_RELEVANCE_RANGE.max.toLocaleString("en-US")}.
                  </div>
                )}
              </div>

              {perspectiveMode === "relevant" && (
                <div
                  key={`relevant-${relevantAnimationKey}`}
                  className="perspective-mode-panel perspective-mode-panel-animated"
                >
                  {!relevantInsights.length ? (
                    <StatusMessage
                      title="No cards in the configured relevance range."
                      description="Switch to Complete mode to view all comparisons."
                      minHeightRem={6}
                    />
                  ) : (
                    <div className="insight-grid insight-grid-flat">
                      {relevantInsights.map((insight, index) =>
                        renderInsightCard(insight, index, insight.id),
                      )}
                    </div>
                  )}
                </div>
              )}

              {perspectiveMode === "complete" && (
                <div
                  key="complete"
                  className="perspective-mode-panel perspective-mode-panel-animated"
                >
                  {perspective.sections.map((section) => (
                    <section className="perspective-section" key={section.id}>
                      <div className="section-head">
                        <h3>{section.title}</h3>
                        <p>{section.description}</p>
                      </div>
                      <div className="insight-grid">
                        {section.insights.map((insight, index) =>
                          renderInsightCard(insight, index, `${section.id}-${insight.id}`),
                        )}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <Layout>
      <ContentArea isTopArea>
        {renderBody()}
      </ContentArea>
    </Layout>
  );
};
